/**
 * Facial-recognition helpers built on @vladmandic/face-api (a maintained fork
 * of face-api.js that bundles its own TensorFlow.js). Everything here runs in
 * the browser and only ever produces a "face descriptor" — a 128-number
 * embedding of a face. Raw images are never uploaded.
 *
 * Two callers:
 *   - The admin new-user wizard turns an uploaded photo into a descriptor
 *     (descriptorFromImage) and sends it to the server to enroll an officer.
 *   - The sign-in second factor turns a live webcam frame into a descriptor
 *     (descriptorFromVideo) and POSTs it to /me/face/verify, where the match
 *     happens server-side against the stored reference.
 *
 * face-api bundles TensorFlow.js and is ~3 MB, so it's imported dynamically:
 * it lands in its own lazy chunk, out of the main app bundle, loaded only when
 * a camera/photo is actually used.
 */

type FaceApi = typeof import("@vladmandic/face-api");

let apiPromise: Promise<FaceApi> | null = null;
function getFaceApi(): Promise<FaceApi> {
  if (!apiPromise) apiPromise = import("@vladmandic/face-api");
  return apiPromise;
}

const MODEL_URL = "/models";

let loadPromise: Promise<void> | null = null;

/** Load the detection, landmark and recognition models once (idempotent). */
export function loadFaceModels(): Promise<void> {
  if (!loadPromise) {
    loadPromise = (async () => {
      const faceapi = await getFaceApi();
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
    })().catch((err) => {
      // Reset so a later retry can re-attempt loading rather than reusing a
      // permanently-rejected promise.
      loadPromise = null;
      throw err;
    });
  }
  return loadPromise;
}

type FaceInput = HTMLVideoElement | HTMLImageElement | HTMLCanvasElement;

/**
 * Detect a single face in the given media element and return its descriptor.
 * Returns null if no face (or more than one) is clearly visible — the caller
 * should ask the user to reposition or pick another photo.
 */
async function descriptorFrom(input: FaceInput): Promise<Float32Array | null> {
  const faceapi = await getFaceApi();
  const result = await faceapi
    .detectSingleFace(input, new faceapi.TinyFaceDetectorOptions())
    .withFaceLandmarks()
    .withFaceDescriptor();
  return result ? result.descriptor : null;
}

export function descriptorFromVideo(video: HTMLVideoElement): Promise<Float32Array | null> {
  return descriptorFrom(video);
}

export function descriptorFromImage(img: HTMLImageElement): Promise<Float32Array | null> {
  return descriptorFrom(img);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't read that image."));
    img.src = src;
  });
}

// Enrolment photos are normalized to a downscaled JPEG before upload: this
// caps the payload, guarantees a content type the server accepts, and sidesteps
// odd phone formats (HEIC etc.) that the canvas re-encodes to JPEG.
const MAX_IMAGE_DIM = 1000;
const JPEG_QUALITY = 0.9;

export interface FaceEnrollPayload {
  /** 128-float descriptor used for matching, or null if no single face found. */
  descriptor: Float32Array | null;
  /** Base64 JPEG (no data: prefix) — what gets sent to the server to encrypt. */
  image: string;
  contentType: "image/jpeg";
  /** Full data URL of the normalized JPEG, for an on-screen preview. */
  dataUrl: string;
}

function encodeJpeg(img: HTMLImageElement): string {
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;
  const scale = Math.min(1, MAX_IMAGE_DIM / Math.max(w, h));
  w = Math.round(w * scale);
  h = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported.");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

/**
 * Decode an uploaded image file once and return BOTH the single-face descriptor
 * (for matching) and a normalized JPEG of the photo (for encrypted storage).
 * The descriptor is computed from the full-resolution image; the stored JPEG is
 * downscaled. Loads the models on demand.
 */
export async function enrollFromFile(file: File): Promise<FaceEnrollPayload> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    await loadFaceModels();
    const descriptor = await descriptorFromImage(img);
    const dataUrl = encodeJpeg(img);
    const comma = dataUrl.indexOf(",");
    return {
      descriptor,
      image: dataUrl.slice(comma + 1),
      contentType: "image/jpeg",
      dataUrl,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Descriptors travel to the API as plain number arrays. */
export function toArray(descriptor: Float32Array): number[] {
  return Array.from(descriptor);
}
